/**
 * Geo-based search utilities for products
 * Includes Haversine distance calculation and coordinate-based filtering
 */

/**
 * Convert degrees to radians
 */
export function toRad(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Calculate distance between two points using Haversine formula
 * @param lat1 - Latitude of first point
 * @param lon1 - Longitude of first point
 * @param lat2 - Latitude of second point
 * @param lon2 - Longitude of second point
 * @returns Distance in kilometers
 */
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371; // Earth radius in km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Filter branches by distance from a point
 */
export function filterBranchesByRadius(
  branches: Array<{ id: string; latitude: string | number; longitude: string | number }>,
  centerLat: number,
  centerLon: number,
  radiusKm: number
): Array<{ id: string; latitude: number; longitude: number; distance: number }> {
  return branches
    .map((branch) => {
      const lat = typeof branch.latitude === "string"
        ? parseFloat(branch.latitude)
        : branch.latitude;
      const lon = typeof branch.longitude === "string"
        ? parseFloat(branch.longitude)
        : branch.longitude;

      if (isNaN(lat) || isNaN(lon)) return null;

      const distance = calculateDistance(centerLat, centerLon, lat, lon);
      if (distance > radiusKm) return null;

      return {
        id: branch.id,
        latitude: lat,
        longitude: lon,
        distance,
      };
    })
    .filter((b): b is NonNullable<typeof b> => b !== null)
    .sort((a, b) => a.distance - b.distance);
}

/**
 * Calculate bounding box for a given center point and radius
 * Used for pre-filtering database queries before precise distance calculation
 * @param lat - Center latitude
 * @param lon - Center longitude
 * @param radiusKm - Radius in kilometers
 * @returns Bounding box coordinates
 */
export function getBoundingBox(
  lat: number,
  lon: number,
  radiusKm: number
): {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
} {
  // 1 degree of latitude ≈ 111 km
  const latDelta = radiusKm / 111.0;
  // 1 degree of longitude varies by latitude
  const lonDelta = radiusKm / (111.0 * Math.cos(toRad(lat)));

  return {
    minLat: lat - latDelta,
    maxLat: lat + latDelta,
    minLon: lon - lonDelta,
    maxLon: lon + lonDelta,
  };
}

/**
 * Check if a point is within bounds
 */
export function isWithinBounds(
  lat: number,
  lng: number,
  bounds: {
    ne_lat: number;
    ne_lng: number;
    sw_lat: number;
    sw_lng: number;
  }
): boolean {
  return (
    lat >= bounds.sw_lat &&
    lat <= bounds.ne_lat &&
    lng >= bounds.sw_lng &&
    lng <= bounds.ne_lng
  );
}
